import React from "react";
import { BillingPaymentStatus } from "@/types/billing-transactions";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PaymentStatusCellProps {
  paymentStatus: BillingPaymentStatus;
  setPaymentStatus: (status: BillingPaymentStatus) => void;
}

const PAYMENT_STATUSES = Object.values(BillingPaymentStatus);

const PAYMENT_STATUS_LABEL_KEYS: Record<BillingPaymentStatus, string> = {
  [BillingPaymentStatus.SUCCEEDED]: "requests.form.paymentStatuses.succeeded",
  [BillingPaymentStatus.NOT_REQUIRED]:
    "requests.form.paymentStatuses.notRequired",
  [BillingPaymentStatus.FAILED]: "requests.form.paymentStatuses.failed",
  [BillingPaymentStatus.PENDING]: "requests.form.paymentStatuses.pending",
  [BillingPaymentStatus.REFUNDED]: "requests.form.paymentStatuses.refunded",
  [BillingPaymentStatus.PARTIALLY_REFUNDED]:
    "requests.form.paymentStatuses.partiallyRefunded",
};

const isBillingPaymentStatus = (value: string): value is BillingPaymentStatus =>
  PAYMENT_STATUSES.some((status) => status === value);

export const PaymentStatusField = ({
  paymentStatus,
  setPaymentStatus,
}: PaymentStatusCellProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="request-payment-status">
        {t("requests.form.paymentStatus")}
      </Label>
      <Select
        value={paymentStatus}
        onValueChange={(value) => {
          if (isBillingPaymentStatus(value)) {
            setPaymentStatus(value);
          }
        }}
      >
        <SelectTrigger
          id="request-payment-status"
          className="w-full justify-between"
        >
          <SelectValue placeholder={t("requests.form.paymentStatus")} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {PAYMENT_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {t(PAYMENT_STATUS_LABEL_KEYS[status])}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
};
